import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, MessageSquare, Save, RotateCcw, Eye, Zap, AlertTriangle, X } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import UpgradeCard from "../../components/UpgradeCard";
import {
  listTemplates, saveTemplate, restoreDefaultTemplate, previewTemplate,
  automationStatus, listAutomationRules, saveAutomationRule,
} from "../../lib/communications";

function planErrorDetail(e) {
  const detail = e?.response?.data?.detail;
  return detail && typeof detail === "object" && detail.error_code === "plan_upgrade_required" ? detail : null;
}

const TIMING_REFERENCES = [
  { value: "confirmation_sent", label: "confirmation was sent" },
  { value: "pickup", label: "pickup" },
  { value: "return", label: "return" },
  { value: "deposit_requested", label: "deposit was requested" },
  { value: "completion", label: "booking completed" },
];

export default function CommunicationSettings() {
  const { user } = useAuth();
  const isOwner = (user?.account_type || user?.role) === "business_owner";
  const [templates, setTemplates] = useState([]);
  const [rules, setRules] = useState([]);
  const [scheduler, setScheduler] = useState({ scheduler_connected: false });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {communication_type, language}
  const [draftSubject, setDraftSubject] = useState("");
  const [draftBody, setDraftBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [planError, setPlanError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, r, s] = await Promise.all([listTemplates(), listAutomationRules(), automationStatus()]);
      setTemplates(t);
      setRules(r);
      setScheduler(s);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startEdit = (type, lang, current) => {
    setEditing({ communication_type: type, language: lang });
    setDraftSubject(current.subject || "");
    setDraftBody(current.body || "");
  };

  const save = async () => {
    setSaving(true);
    try {
      await saveTemplate(editing.communication_type, editing.language, draftSubject, draftBody);
      toast.success("Template saved");
      setEditing(null);
      await load();
    } catch (e) {
      const pe = planErrorDetail(e);
      if (pe) { setPlanError(pe); setEditing(null); }
      else toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const restore = async (type, lang) => {
    try {
      await restoreDefaultTemplate(type, lang);
      toast.success("Restored default wording");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const preview = async (type, lang) => {
    try {
      const data = await previewTemplate(type, lang);
      toast.message(data.subject || "Preview", { description: data.body });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleAutomation = async (type, rule, enabled) => {
    try {
      await saveAutomationRule(type, {
        enabled,
        reference: rule.timing.reference, direction: rule.timing.direction,
        hours: rule.timing.hours, language_default: rule.language_default || "en",
      });
      toast.success(enabled ? "Automation enabled" : "Automation turned off");
      await load();
    } catch (e) {
      const pe = planErrorDetail(e);
      if (pe) setPlanError(pe);
      else toast.error(formatApiError(e));
    }
  };

  const updateTiming = async (type, rule, patch) => {
    try {
      await saveAutomationRule(type, {
        enabled: rule.enabled,
        reference: patch.reference ?? rule.timing.reference,
        direction: patch.direction ?? rule.timing.direction,
        hours: patch.hours ?? rule.timing.hours,
        language_default: rule.language_default || "en",
      });
      await load();
    } catch (e) {
      const pe = planErrorDetail(e);
      if (pe) setPlanError(pe);
      else toast.error(formatApiError(e));
    }
  };

  if (loading) return <div className="max-w-4xl text-sm text-slate-500">Loading…</div>;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="communication-settings-page">
      <Link to="/app/settings" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900">
        <ArrowLeft className="w-4 h-4" /> Back to Settings
      </Link>

      <div>
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Bookings</div>
        <h1 className="font-display text-3xl font-bold mt-1 inline-flex items-center gap-2">
          <MessageSquare className="w-6 h-6 text-[#1B4332]" /> Message Templates &amp; Automation
        </h1>
        <p className="text-sm text-slate-500 mt-1 max-w-2xl">
          One place to review and customize every customer message ConfirmPh can prepare. Sending stays a staff
          decision by default: automation below only runs when you explicitly turn it on for that message type.
        </p>
      </div>

      {planError && (
        <div className="relative">
          <button onClick={() => setPlanError(null)} data-testid="comms-plan-error-dismiss"
                  className="absolute top-3 right-3 p-1 rounded-md hover:bg-slate-100 text-slate-500 z-10">
            <X className="w-3.5 h-3.5" />
          </button>
          <UpgradeCard
            detail={planError} isOwner={isOwner}
            title="Unlock editable templates & automation"
            features={[
              "Pro: customize wording, EN/TL variants, preview before send",
              "Enterprise/Custom: automation rules and timing per message type",
            ]}
          />
        </div>
      )}

      {!scheduler.scheduler_connected && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 flex items-start gap-2"
             data-testid="automation-scheduler-banner">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <b>Automated sends are configured but not yet active.</b> No scheduler is connected to this ConfirmPh
            instance yet, so turning on automation below prepares the rule but nothing will send on a timer until
            that infrastructure is connected. Staff can still send every message manually at any time.
          </div>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
        {templates.map((t) => {
          const rule = rules.find((r) => r.communication_type === t.communication_type);
          return (
            <div key={t.communication_type} className="p-5" data-testid={`template-row-${t.communication_type}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="font-semibold text-sm">{t.label}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {["en", "tl"].map((lang) => (
                      <span key={lang} className="mr-3">
                        {lang.toUpperCase()}: {t.languages[lang]?.is_customized ? "Customized" : "Default"}
                      </span>
                    ))}
                  </div>
                </div>
                {t.automatable && rule && (
                  <AutomationToggle type={t.communication_type} rule={rule}
                                     onToggle={(enabled) => toggleAutomation(t.communication_type, rule, enabled)}
                                     onTimingChange={(patch) => updateTiming(t.communication_type, rule, patch)} />
                )}
              </div>

              <div className="flex flex-wrap gap-2 mt-3">
                {["en", "tl"].map((lang) => (
                  <div key={lang} className="flex items-center gap-1">
                    <span className="text-[11px] text-slate-400 uppercase">{lang}</span>
                    <button onClick={() => preview(t.communication_type, lang)}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-slate-300 text-xs hover:bg-slate-50">
                      <Eye className="w-3 h-3" /> Preview
                    </button>
                    <button onClick={() => startEdit(t.communication_type, lang, t.languages[lang])}
                            data-testid={`template-edit-${t.communication_type}-${lang}`}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-slate-300 text-xs hover:bg-slate-50">
                      Edit
                    </button>
                    {t.languages[lang]?.is_customized && (
                      <button onClick={() => restore(t.communication_type, lang)}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-slate-300 text-xs hover:bg-slate-50">
                        <RotateCcw className="w-3 h-3" /> Restore default
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {editing?.communication_type === t.communication_type && (
                <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2"
                     data-testid={`template-editor-${t.communication_type}`}>
                  <div className="text-xs text-slate-500">
                    Editing {editing.language.toUpperCase()} · Available fields: {t.available_fields.map((f) => `{${f}}`).join(", ")}
                  </div>
                  <label className="block">
                    <span className="text-[11px] uppercase tracking-wider text-slate-500">Subject (email only)</span>
                    <input value={draftSubject} onChange={(e) => setDraftSubject(e.target.value)}
                           className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm" />
                  </label>
                  <label className="block">
                    <span className="text-[11px] uppercase tracking-wider text-slate-500">Body</span>
                    <textarea rows={4} value={draftBody} onChange={(e) => setDraftBody(e.target.value)}
                              className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm" />
                  </label>
                  <div className="flex justify-end gap-2">
                    <button onClick={() => setEditing(null)} className="px-3 py-1.5 text-sm rounded-md hover:bg-slate-100">Cancel</button>
                    <button onClick={save} disabled={saving}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-md bg-[#1B4332] text-white hover:opacity-90 disabled:opacity-50">
                      <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AutomationToggle({ type, rule, onToggle, onTimingChange }) {
  return (
    <div className="flex items-center gap-2 text-xs" data-testid={`automation-toggle-${type}`}>
      <Zap className={`w-3.5 h-3.5 ${rule.enabled ? "text-[#1B4332]" : "text-slate-300"}`} />
      <button onClick={() => onToggle(!rule.enabled)}
              className={`px-2 py-1 rounded-md border ${rule.enabled ? "bg-[#1B4332] text-white border-[#1B4332]" : "bg-white border-slate-300"}`}>
        Automation: {rule.enabled ? "On" : "Off"}
      </button>
      {rule.enabled && (
        <>
          <input type="number" step="0.5" value={rule.timing.hours}
                 onChange={(e) => onTimingChange({ hours: Number(e.target.value) })}
                 className="w-16 rounded-md border border-slate-300 px-1.5 py-1 text-xs" />
          <span>h</span>
          <select value={rule.timing.direction} onChange={(e) => onTimingChange({ direction: e.target.value })}
                  className="rounded-md border border-slate-300 px-1.5 py-1 text-xs">
            <option value="before">before</option>
            <option value="after">after</option>
          </select>
          <select value={rule.timing.reference} onChange={(e) => onTimingChange({ reference: e.target.value })}
                  className="rounded-md border border-slate-300 px-1.5 py-1 text-xs">
            {TIMING_REFERENCES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </>
      )}
    </div>
  );
}
